const MAX_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
const TARGET_UPLOAD_SIZE_BYTES = Math.floor(4.5 * 1024 * 1024);
const MAX_DIMENSION = 2200;
const MIN_QUALITY = 0.55;
const QUALITY_STEP = 0.08;
const SUPPORTED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
  'image/x-heic',
  'image/x-heif',
]);
const CANVAS_COMPRESSIBLE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

const formatFileSize = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)}MB`;

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read the selected image.'));
    reader.readAsDataURL(file);
  });

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not process the selected image.'));
    image.src = src;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error('Could not prepare the image for upload.'));
    }, type, quality);
  });

const getScaledDimensions = (width: number, height: number) => {
  const largestSide = Math.max(width, height);
  if (largestSide <= MAX_DIMENSION) {
    return { width, height };
  }

  const scale = MAX_DIMENSION / largestSide;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

export const getImageUploadError = (file: File) => {
  if (!SUPPORTED_IMAGE_TYPES.has(file.type)) {
    return 'Invalid file type. Please upload a JPEG, PNG, GIF, WebP, or HEIC image.';
  }
  return null;
};

export const prepareImageForUpload = async (file: File) => {
  const typeError = getImageUploadError(file);
  if (typeError) {
    throw new Error(typeError);
  }

  if (file.size <= MAX_UPLOAD_SIZE_BYTES) {
    return file;
  }

  if (!CANVAS_COMPRESSIBLE_TYPES.has(file.type)) {
    throw new Error(
      `This image is ${formatFileSize(file.size)}. Please compress it to under 5MB before uploading.`,
    );
  }

  const image = await loadImage(await readFileAsDataUrl(file));
  const { width, height } = getScaledDimensions(image.naturalWidth, image.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not prepare the image for upload.');
  }

  context.drawImage(image, 0, 0, width, height);

  const outputType = file.type === 'image/png' ? 'image/jpeg' : file.type;
  const outputName = outputType === file.type
    ? file.name
    : file.name.replace(/\.[^.]+$/, '') + '.jpg';

  let quality = 0.9;
  let bestBlob = await canvasToBlob(canvas, outputType, outputType === 'image/jpeg' || outputType === 'image/webp' ? quality : undefined);

  while (bestBlob.size > TARGET_UPLOAD_SIZE_BYTES && quality > MIN_QUALITY && (outputType === 'image/jpeg' || outputType === 'image/webp')) {
    quality = Math.max(MIN_QUALITY, quality - QUALITY_STEP);
    bestBlob = await canvasToBlob(canvas, outputType, quality);
  }

  const optimizedFile = new File([bestBlob], outputName, {
    type: outputType,
    lastModified: Date.now(),
  });

  if (optimizedFile.size > MAX_UPLOAD_SIZE_BYTES) {
    throw new Error(
      `This image is still ${formatFileSize(optimizedFile.size)} after compression. Please choose a smaller image or compress it manually.`,
    );
  }

  return optimizedFile;
};
